import { useEffect, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UserPlus, X } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function TeamCard({ projectId, team, onChange }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState("");
  const [asLead, setAsLead] = useState(false);
  const canManage = user && ["lead", "project_manager", "admin"].includes(user.role);

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  const add = async () => {
    if (!pick) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/team`, { user_id: pick, is_lead: asLead });
      onChange(data.assigned_team);
      setPick("");
      setAsLead(false);
      toast.success("Team member added");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (uid) => {
    try {
      const { data } = await api.delete(`/projects/${projectId}/team/${uid}`);
      onChange(data.assigned_team);
      toast.success("Removed");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const available = users.filter((u) => !(team || []).some((t) => t.user_id === u.id));

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid="team-card">
      <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Assigned Team</div>
      {(team || []).length === 0 && <div className="text-[11px] text-[var(--c-muted)] mb-2">No members yet — handover packs are addressed by discipline.</div>}
      <div className="space-y-1.5 mb-3">
        {(team || []).map((t) => (
          <div key={t.user_id} className="flex items-center gap-2 text-xs border border-[var(--c-border)] rounded-sm px-2.5 py-1.5"
            data-testid={`team-member-${t.user_id.slice(0, 8)}`}>
            <span className="text-[var(--c-text)] flex-1">{t.name}</span>
            <span className="font-mono text-[9px] text-[var(--c-muted)]">{t.role}</span>
            <span className="font-mono text-[9px] text-[var(--c-accent)]">{t.discipline || "—"}</span>
            {t.is_lead && <span className="font-mono text-[8px] uppercase tracking-wider text-[var(--c-amber)] border border-[var(--c-amber)]/40 rounded-sm px-1">handover lead</span>}
            {canManage && (
              <button data-testid={`team-remove-${t.user_id.slice(0, 8)}`} onClick={() => remove(t.user_id)}
                className="text-[var(--c-muted)] hover:text-[var(--c-red)]"><X size={11} /></button>
            )}
          </div>
        ))}
      </div>
      {canManage && (
        <div className="flex gap-2 items-center">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="team-add-user" className="flex-1 h-7 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
              <SelectValue placeholder="Add member…" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
              {available.map((u) => (
                <SelectItem key={u.id} value={u.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] text-xs">
                  {u.name} <span className="font-mono text-[var(--c-muted)]">· {u.discipline || u.role}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1 text-[10px] text-[var(--c-muted)] shrink-0">
            <input type="checkbox" data-testid="team-lead-check" checked={asLead} onChange={(e) => setAsLead(e.target.checked)}
              className="accent-[var(--c-amber)]" /> Lead
          </label>
          <Button size="sm" data-testid="team-add-btn" onClick={add} disabled={!pick}
            className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-7 text-[10px] shrink-0">
            <UserPlus size={11} className="mr-1" /> Add
          </Button>
        </div>
      )}
    </div>
  );
}
